import { RequestDetailsHeader, RequestDetailsSection } from "../RequestDetailsContent";
import {
  RequestCardReason,
  RequestInfoItem,
  RequestInfoGrid,
  RequestCardHeader,
  RequestStatusStrip,
} from "../RequestCardContent";
import { useEffect, useMemo, useState } from "react";
import {
  GcpResponseProps,
  ResourceRequestsProps,
} from "../resourceRequestInterface";
import ResourceRequestToolbar from "../ResourceRequestToolbar";
import { myLocalStorage } from "../../../StorageHelper";
import useUserStore from "../../../../services/userStore";
import {
  AlertTriangle,
  CheckCircle2,
  CheckCircle2Icon,
  Clock,
  Cloud,
  Eye,
  Layers,
  RotateCcw,
  User,
  XCircle,
  CloudOff,
} from "lucide-react";
import { changeGCPStatus, getGCPNotificationAPI, getGCPNotificationCountAPI, revokeRequest } from "../Api/resourcerequestapi";
import { format } from "date-fns";
import { Card, CardContent } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { CLOUD_UNCONFIGURED_STATUS, formatRequestStatus, getStatusColor } from "../utils";
import { Button } from "../../../ui/button";
import { Dialog, DialogContent } from "../../../ui/dialog";
import { Skeleton } from "../../../ui/skeleton";
import { formatDate } from "../../formatTimeAgo";
import MultiApprovalProgressPanel from "../../MultiApprovals/MultiApprovalProgressPanel";
import MultiApprovalGroupedHistory from "../../MultiApprovals/MultiApprovalGroupedHistory";
import MultiApprovalListSummary from "../../MultiApprovals/MultiApprovalListSummary";
import { isMultiApprovalRequest } from "../../MultiApprovals/Api/multiapprovalapi";
import { useMultiApprovalWorkflow } from "../hooks/useMultiApprovalWorkflow";
import { RESOURCE_REQUEST_POLL_MS } from "../resourceRequestPolling";

export const GCPRequestDetails = ({
  computerName = "",
  searchTerm = "",
  statusFilter = "",
  privilegeFilter = "all",
  startDate,
  endDate,
  activeTab,
  onSearchChange
}: ResourceRequestsProps) => {
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<GcpResponseProps[]>([]);
  const [selectedRequest, setSelectedRequest] =
    useState<GcpResponseProps | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const tenantName = useMemo(
    () => myLocalStorage.getItem("latestTenant").tenantName || "",
    []
  );
  const { email, role } = useUserStore((state) => state.user);
  const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set());

  const {
    detailsWorkflow,
    openDetailsWorkflow,
    closeDetailsWorkflow,
    canUserApprove,
    canUserDecline,
    canUserDeclineDetails,
    getWorkflowForRequest,
  } = useMultiApprovalWorkflow(notifications);

  const handleRequest = async (
    status: string,
    id: number,
    notification: GcpResponseProps
  ) => {
    setLoadingIds((current) => new Set(current).add(id));
    try {
      await changeGCPStatus(status, id, tenantName, notification.projectId, notification);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }

    await getGCPNotification(false);
  };

  const handleRevoke = async (accessId: number) => {
    setLoadingIds((current) => new Set(current).add(accessId));
    try {
      const revoked = await revokeRequest("gcp", accessId);
      if (revoked !== true) {
        throw new Error("Failed to cancel or revoke GCP request");
      }
      setIsDetailsOpen(false);
      await getGCPNotification(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingIds((current) => {
        const next = new Set(current);
        next.delete(accessId);
        return next;
      });
    }
  };

  const fetchGCPCount = async () => {
    const response = await getGCPNotificationCountAPI(
      computerName,
      searchTerm,
      statusFilter.toUpperCase(),
      startDate ? format(startDate, "yyyy-MM-dd") : "",
      endDate ? format(endDate, "yyyy-MM-dd") : "",
      tenantName,
      page,
      email,
      role,
        privilegeFilter
      )
    setTotalItems(response || 0);
  }

  const getGCPNotification = async (isFirst = true) => {
    setIsLoading(isFirst);
    try {
      const response: GcpResponseProps[] = await getGCPNotificationAPI(
        computerName,
        searchTerm,
        statusFilter.toUpperCase(),
        startDate ? format(startDate, "yyyy-MM-dd") : "",
        endDate ? format(endDate, "yyyy-MM-dd") : "",
        tenantName,
        page - 1,
        email,
        role,
      privilegeFilter
    );
      setNotifications(response);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };



  const handleViewDetails = (request: GcpResponseProps) => {
    setSelectedRequest(request);
    setIsDetailsOpen(true);
    openDetailsWorkflow(request);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "PENDING":
        return <Clock className="w-4 h-4" />;
      case "APPROVED":
        return <CheckCircle2Icon className="w-4 h-4" />;
      case "DECLINED":
        return <XCircle className="w-4 h-4" />;
      case "REVOKED":
        return <RotateCcw className="w-4 h-4" />;
      case "EXPIRED":
        return <AlertTriangle className="w-4 h-4" />;
      case CLOUD_UNCONFIGURED_STATUS:
        return <CloudOff className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  useEffect(() => {
    fetchGCPCount()
    getGCPNotification();
    if (activeTab === "gcp") {
      const interval = setInterval(() => {
        fetchGCPCount()
        getGCPNotification(false);
      }, RESOURCE_REQUEST_POLL_MS)
      return () => clearInterval(interval)
    }
  }, [
    computerName,
    searchTerm,
    statusFilter,
    privilegeFilter,
    startDate,
    endDate,
    tenantName,
    page,
    email,
    role,
    activeTab
  ]);
  const totalPages = Math.ceil(totalItems / 15);

  // ✅ Skeleton Loader UI
  const renderSkeleton = () => (
    <div className="space-y-3">
      {[...Array(1)].map((_, i) => (
        <Card
          key={i}
          className="request-card border-slate-200 dark:border-slate-700 p-4 space-y-3"
        >
          <div className="flex justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4 rounded-full" />
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton className="h-5 w-20 rounded-md" />
          </div>

          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-24" />
          </div>

          <RequestInfoGrid className="grid-cols-2 md:grid-cols-4">
            {[...Array(4)].map((_, j) => (
              <div key={j}>
                <Skeleton className="h-3 w-24 mb-1" />
                <Skeleton className="h-4 w-32" />
              </div>
            ))}
          </RequestInfoGrid>

          <RequestInfoGrid className="grid-cols-2 md:grid-cols-3">
            {[...Array(3)].map((_, j) => (
              <div key={j}>
                <Skeleton className="h-3 w-20 mb-1" />
                <Skeleton className="h-4 w-28" />
              </div>
            ))}
          </RequestInfoGrid>

          <div className="flex justify-end gap-2 pt-2">
            <Skeleton className="h-6 w-20 rounded-md" />
            <Skeleton className="h-6 w-20 rounded-md" />
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <>
      <div className="resource-request-list space-y-1">
        {onSearchChange && (
          <ResourceRequestToolbar
            page={page}
            totalItems={totalItems}
            onPageChange={setPage}
            searchTerm={searchTerm}
            onSearchChange={(value) => {
              setPage(1);
              onSearchChange(value);
            }}
          />
        )}
        {isLoading ? (
          renderSkeleton()
        ) : notifications.length > 0 ? (
          <>
            {notifications.map((request) => (
              <Card
                key={request.id}
                className="request-card border-slate-200 dark:border-slate-700"
              >
                <RequestStatusStrip status={request.requestStatus} />
                <CardContent className="request-card-content p-2">
                  <div className="flex items-start gap-3">
                    <div className="request-card-body flex-1 space-y-1">
                      <RequestCardHeader
                        icon={<Cloud className="w-3.5 h-3.5 text-green-500" />}
                        title={<>{request.accessName}</>}
                        status={
                          <Badge
                            className={`border text-xs px-1.5 py-0.5 ${getStatusColor(
                              request.requestStatus,
                            )}`}
                          >
                            <div className="flex items-center gap-1">
                              {getStatusIcon(request.requestStatus)}
                              {formatRequestStatus(request.requestStatus)}
                            </div>
                          </Badge>
                        }
                        subtitle={
                          <p className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                            <User className="w-4 h-4" />
                            {request.email}
                            <span className="text-slate-400">•</span>
                            {formatDate(request.createdAt)}
                          </p>
                        }
                        actions={
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(request)}
                            className="h-auto px-2 py-1 text-xs"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Details
                          </Button>
                        }
                      />

                      <RequestInfoGrid className="grid-cols-2 md:grid-cols-4">
                        <RequestInfoItem label="GCP User Email">
                          <p className="font-medium text-slate-700 dark:text-slate-200">
                            {request.gcpUserEmail}
                          </p>
                        </RequestInfoItem>
                        <RequestInfoItem label="Role ID">
                          <p className="text-slate-700 dark:text-slate-200">
                            {request.roleId}
                          </p>
                        </RequestInfoItem>
                        <RequestInfoItem label="Resource Type">
                          <p className="text-slate-700 dark:text-slate-200">
                            {request.gcpResourceType}
                          </p>
                        </RequestInfoItem>
                        <RequestInfoItem label="Duration">
                          <p className="text-slate-700 dark:text-slate-200">
                            {request.privilegeLevel === "PERMANENT"
                              ? "Permanent"
                              : `${request.selectedTime} min`}
                          </p>
                        </RequestInfoItem>
                      </RequestInfoGrid>

                      <RequestInfoGrid className="grid-cols-2 md:grid-cols-3" context>
                        <RequestInfoItem label="Project ID">
                          <p className="font-mono text-xs text-green-800 dark:text-green-200">
                            {request.projectId}
                          </p>
                        </RequestInfoItem>
                        <RequestInfoItem label="Resource Name">
                          <p className="text-xs text-green-800 dark:text-green-200">
                            {request.accessName}
                          </p>
                        </RequestInfoItem>
                      </RequestInfoGrid>

                      {(() => {
                        const workflow = getWorkflowForRequest(request.id);
                        return isMultiApprovalRequest(workflow) ? (
                          <MultiApprovalListSummary workflow={workflow} />
                        ) : null;
                      })()}

                      <div className="request-card-footer flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
                        <RequestCardReason>{request.requestMessage}</RequestCardReason>
                        <div className="flex items-center gap-2">
                          {request.requestStatus === "PENDING" && (
                            <>
                              {role !== "TENANT_USER" ? (
                                <>
                                  <Button
                                    disabled={
                                      loadingIds.has(request.id) ||
                                      !canUserApprove(request.id)
                                    }
                                    size="sm"
                                    className="text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                    onClick={() =>
                                      handleRequest("APPROVED", request.id, request)
                                    }
                                  >
                                    {loadingIds.has(request.id) ? (
                                      <>
                                        <svg
                                          className="animate-spin w-4 h-4 mr-1 text-white"
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                          ></circle>
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                          ></path>
                                        </svg>
                                        Approving...
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    disabled={
                                      loadingIds.has(request.id) ||
                                      !canUserDecline(request.id)
                                    }
                                    className="disabled:opacity-50"
                                    onClick={() =>
                                      handleRequest("DECLINE", request.id, request)
                                    }
                                  >
                                    <XCircle className="w-4 h-4 mr-1" /> Decline
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={loadingIds.has(request.id)}
                                    className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                                    onClick={() => handleRevoke(request.id)}
                                  >
                                    <RotateCcw className="w-4 h-4 mr-1" /> Cancel Request
                                  </Button>
                                </>
                              )}
                            </>
                          )}
                          {request.requestStatus === "APPROVED" && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={loadingIds.has(request.id)}
                              className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                              onClick={() => handleRevoke(request.id)}
                              data-testid={`button-revoke-gcp-${request.id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Revoke
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <Card className="request-card request-empty-state border-slate-200 dark:border-slate-700">
            <CardContent className="request-card-content p-12 text-center">
              <Layers className="w-12 h-12 mx-auto mb-4 text-green-300 dark:text-green-600" />
              <h3 className="mb-2 text-lg font-medium text-slate-600 dark:text-slate-300">
                No GCP requests found
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No access requests found for GCP resources
              </p>
            </CardContent>
          </Card>
        )}

        {!onSearchChange && totalPages > 1 && (
          <div className="flex items-center justify-between py-4">
            <div className="text-sm text-slate-600 dark:text-slate-300">
              Showing {(page - 1) * 15 + 1}–{Math.min(page * 15, totalItems)} of {totalItems}
            </div>

            <div className="flex items-center space-x-2">
              {/* Previous Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                data-testid="button-prev-page-onprem"
              >
                Previous
              </Button>

              {/* Dynamic Page Numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
                const pageNum = startPage + i;

                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPage(pageNum)}
                    className={pageNum === page ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                    data-testid={`page-onprem-${pageNum}`}
                  >
                    {pageNum}
                  </Button>
                );
              })}

              {/* Next Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                data-testid="button-next-page-onprem"
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Dialog remains unchanged */}
        <Dialog
          open={isDetailsOpen}
          onOpenChange={(open) => {
            setIsDetailsOpen(open);
            if (!open) {
              closeDetailsWorkflow();
            }
          }}
        >
          <DialogContent className="request-details-dialog">
            <RequestDetailsHeader
              icon={<Layers className="w-5 h-5 text-green-500" />}
              title="GCP Access Request Details"
            >
              Complete information for GCP request #{selectedRequest?.id}
            </RequestDetailsHeader>

            {selectedRequest && (
              <>
                <div className="request-details-body">
                  <RequestDetailsSection title="Request information" variant="request">
                    <RequestInfoGrid className="request-details-grid">
                      <RequestInfoItem label="Request ID">
                        <p className="text-slate-700 dark:text-slate-200 font-mono">
                          {selectedRequest.id}
                        </p>
                      </RequestInfoItem>
                      <RequestInfoItem label="Status">
                        <p className="text-slate-800 dark:text-slate-100">
                          {formatRequestStatus(selectedRequest.requestStatus)}
                        </p>
                      </RequestInfoItem>
                      <RequestInfoItem label="Requested By">
                        <p className="text-slate-700 dark:text-slate-200">
                          {selectedRequest.email}
                        </p>
                      </RequestInfoItem>
                    </RequestInfoGrid>
                  </RequestDetailsSection>
                  <RequestDetailsSection title="GCP project and access">
                    <RequestInfoGrid className="request-details-grid">
                      <RequestInfoItem label="GCP Username">
                        <p className="text-slate-700 dark:text-slate-200">
                          {selectedRequest.gcpUserEmail}
                        </p>
                      </RequestInfoItem>
                      <RequestInfoItem label="Project ID">
                        <p className="text-slate-700 dark:text-slate-200 font-mono text-xs">
                          {selectedRequest.projectId}
                        </p>
                      </RequestInfoItem>
                      <RequestInfoItem label="Access Name">
                        <p className="text-slate-700 dark:text-slate-200">
                          {selectedRequest.accessName}
                        </p>
                      </RequestInfoItem>
                      <RequestInfoItem label="Access Type">
                        <p className="text-slate-700 dark:text-slate-200 text-xs">
                          {selectedRequest.accessType}
                        </p>
                      </RequestInfoItem>
                      <RequestInfoItem label="Resource Type">
                        <p className="text-slate-700 dark:text-slate-200 text-xs">
                          {selectedRequest.gcpResourceType}
                        </p>
                      </RequestInfoItem>
                    </RequestInfoGrid>
                  </RequestDetailsSection>
                  <RequestDetailsSection
                    title="Business Justification"
                    variant="justification"
                  >
                    <p className="request-details-message">
                      {selectedRequest.requestMessage}
                    </p>
                  </RequestDetailsSection>
                  {detailsWorkflow?.groupedApprovals?.some(
                    (group) => group.approvals.length > 0,
                  ) && (
                    <div className="request-details-workflow">
                      <MultiApprovalGroupedHistory
                        groupedApprovals={detailsWorkflow.groupedApprovals}
                      />
                    </div>
                  )}
                  {selectedRequest.requestStatus === "PENDING" &&
                    isMultiApprovalRequest(detailsWorkflow) && (
                      <div className="request-details-workflow">
                        <MultiApprovalProgressPanel
                          ruleProgress={detailsWorkflow.ruleProgress}
                        />
                      </div>
                    )}
                  {/* Admin Action Buttons */}
                </div>
                <div className="request-details-footer">
                  {selectedRequest.requestStatus === "PENDING" && (
                    <div className="request-details-actions">
                      <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>
                        Close
                      </Button>
                      {role === "TENANT_USER" ? (
                        <Button
                          variant="outline"
                          disabled={loadingIds.has(selectedRequest.id)}
                          className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                          onClick={() => handleRevoke(selectedRequest.id)}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Cancel Request
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="destructive"
                            disabled={
                              loadingIds.has(selectedRequest.id) || !canUserDeclineDetails()
                            }
                            className="disabled:opacity-50"
                            onClick={() => {
                              handleRequest("DECLINED", selectedRequest.id, selectedRequest);
                              setIsDetailsOpen(false);
                            }}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Decline
                          </Button>
                          <Button
                            className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
                            disabled={
                              loadingIds.has(selectedRequest.id) ||
                              !canUserApprove(selectedRequest.id)
                            }
                            onClick={() => {
                              handleRequest("APPROVED", selectedRequest.id, selectedRequest);
                            }}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                  {selectedRequest.requestStatus === "APPROVED" && (
                    <div className="request-details-actions">
                      <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>
                        Close
                      </Button>
                      <Button
                        variant="outline"
                        disabled={loadingIds.has(selectedRequest.id)}
                        className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                        onClick={() => handleRevoke(selectedRequest.id)}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Revoke
                      </Button>
                    </div>
                  )}
                  {(selectedRequest.requestStatus === "DECLINED" ||
                    selectedRequest.requestStatus === "REVOKED" ||
                    selectedRequest.requestStatus === "EXPIRED" ||
                    selectedRequest.requestStatus === CLOUD_UNCONFIGURED_STATUS) && (
                    <div className="request-details-actions">
                      <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>
                        Close
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
